import { Link } from 'react-router';
import {
  BoxesIcon,
  ScanLineIcon,
  CheckCircle2Icon,
  CircleIcon,
  ClockIcon,
  TrophyIcon,
  Building2Icon,
  BoxIcon,
  type LucideIcon,
} from 'lucide-react';
import type { PublicStation, PublicChallenge } from '@cdf/api-client-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

// ── ALGIM 2026 Program Schedule ─────────────────────────────────────────

export interface ProgramSession {
  time: string;
  title: string;
  room: string;
  icon: LucideIcon;
}

export const ALGIM_PROGRAM: ProgramSession[] = [
  { time: '8:30 AM', title: 'Registration & Exhibition Opens', room: 'Sponsors Space', icon: Building2Icon },
  { time: '9:00 AM', title: 'Welcome & Opening Keynote', room: 'Plenary Room', icon: TrophyIcon },
  { time: '10:00 AM', title: 'Digital Transformation Panel', room: 'Expo Hall', icon: BoxIcon },
  { time: '11:00 AM', title: 'Civic Tech Innovation Showcase', room: 'Collins Room', icon: Building2Icon },
  { time: '12:00 PM', title: 'Networking Lunch & Exhibition Floor', room: 'Sponsors Space', icon: BoxesIcon },
  { time: '1:00 PM', title: 'AI in Local Government Workshop', room: 'Swanston Boardroom', icon: BoxIcon },
  { time: '2:00 PM', title: 'Case Studies Roundtable', room: 'Degraves Boardroom', icon: Building2Icon },
  { time: '3:00 PM', title: 'Exhibition Floor & CV Scavenger Hunt', room: 'Sponsors Space', icon: ScanLineIcon },
  { time: '4:00 PM', title: 'Closing Plenary & Awards', room: 'Plenary Room', icon: TrophyIcon },
];

// ── Helpers ──────────────────────────────────────────────────────────────

/** Extract stand number from station name like "Stand 1 — Circle T Software Foundry" */
export function parseStandNumber(name: string): number | null {
  const match = name.match(/Stand\s+(\d+)/i);
  return match?.[1] ? parseInt(match[1], 10) : null;
}

/** Extract exhibitor name from station name */
export function parseExhibitorName(name: string): string {
  const dashIdx = name.indexOf('—');
  if (dashIdx >= 0) return name.slice(dashIdx + 1).trim();
  const hyphenIdx = name.indexOf(' - ');
  if (hyphenIdx >= 0) return name.slice(hyphenIdx + 3).trim();
  return name;
}

/** Check if a station is an exhibitor booth */
export function isBoothStation(station: { name: string }): boolean {
  return parseStandNumber(station.name) !== null;
}

// ── Challenge type metadata ───────────────────────────────────────────────

const CHALLENGE_META: Record<string, { label: string; color: string }> = {
  quiz: { label: 'Quiz', color: 'text-blue-500' },
  demo_report: { label: 'Report', color: 'text-amber-500' },
  ar_view: { label: 'AR', color: 'text-purple-500' },
  map_find: { label: 'Map', color: 'text-green-500' },
  gps_capture: { label: 'GPS', color: 'text-cyan-500' },
  cv_scan: { label: 'CV Scan', color: 'text-violet-500' },
};

// ── Program Timeline ────────────────────────────────────────────────────

export function ProgramTimeline() {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <ClockIcon className="h-4 w-4 text-primary" />
          ALGIM 2026 Program Schedule
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="relative space-y-3 before:absolute before:left-3 before:top-2 before:bottom-2 before:w-px before:bg-border">
          {ALGIM_PROGRAM.map((session, i) => {
            const Icon = session.icon;
            return (
              <div key={i} className="relative flex items-start gap-3 pl-1">
                <div className="z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-border bg-background">
                  <Icon className="h-3 w-3 text-primary" />
                </div>
                <div className="flex-1 pt-0.5">
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs font-semibold text-muted-foreground tabular-nums">{session.time}</span>
                  </div>
                  <p className="text-sm font-medium leading-tight">{session.title}</p>
                  <p className="text-xs text-muted-foreground">{session.room}</p>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Exhibitor Engagement Card ────────────────────────────────────────────

export function ExhibitorCard({
  station,
  challenges,
  captured,
  completedChallengeIds,
}: {
  station: PublicStation;
  challenges: PublicChallenge[];
  captured: boolean;
  completedChallengeIds: string[];
}) {
  const standNum = parseStandNumber(station.name);
  const exhibitorName = parseExhibitorName(station.name);
  const stationChallenges = challenges.filter((c) => c.stationId === station.id);
  const hasCvScan = stationChallenges.some((c) => String(c.type) === 'cv_scan');
  const allDone = stationChallenges.length > 0 && stationChallenges.every((c) => completedChallengeIds.includes(c.id));

  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded-lg border p-3 transition-colors',
        captured ? 'border-primary/40 bg-primary/5' : 'border-border hover:bg-muted/40'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-xs font-bold text-primary tabular-nums">
            {standNum}
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium leading-tight">{exhibitorName}</p>
            <p className="text-xs text-muted-foreground">{station.points} pts</p>
          </div>
        </div>
        {captured ? (
          <CheckCircle2Icon className="h-4 w-4 shrink-0 text-green-500" />
        ) : (
          <CircleIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
      </div>

      {/* Challenge type badges */}
      {stationChallenges.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {stationChallenges.map((ch) => {
            const done = completedChallengeIds.includes(ch.id);
            const meta = CHALLENGE_META[String(ch.type)] ?? { label: ch.type, color: 'text-muted-foreground' };
            return (
              <span
                key={ch.id}
                className={cn(
                  'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                  done ? 'bg-green-500/15 text-green-600 dark:text-green-400' : 'bg-muted text-muted-foreground'
                )}
              >
                {meta.label}
              </span>
            );
          })}
        </div>
      )}

      {/* Quick action buttons */}
      <div className="mt-auto flex gap-1.5">
        {hasCvScan && !allDone && (
          <Button asChild size="sm" variant="outline" className="h-7 px-2 text-xs">
            <Link to="/ar">
              <ScanLineIcon className="mr-1 h-3 w-3" />
              Scan CV
            </Link>
          </Button>
        )}
        <Button asChild size="sm" variant="ghost" className="h-7 px-2 text-xs">
          <Link to="/events/novotel-collins-conference/twin">
            <BoxesIcon className="mr-1 h-3 w-3" />
            3D
          </Link>
        </Button>
      </div>
    </div>
  );
}

// ── Booth Directory ──────────────────────────────────────────────────────

export function BoothDirectory({
  stations,
  challenges,
  capturedStationIds,
  completedChallengeIds,
}: {
  stations: PublicStation[];
  challenges: PublicChallenge[];
  capturedStationIds: string[];
  completedChallengeIds: string[];
}) {
  const boothStations = stations
    .filter((s) => isBoothStation(s))
    .sort((a, b) => (parseStandNumber(a.name) ?? 0) - (parseStandNumber(b.name) ?? 0));

  if (boothStations.length === 0) return null;

  const capturedCount = boothStations.filter((s) => capturedStationIds.includes(s.id)).length;
  const pct = Math.round((capturedCount / boothStations.length) * 100);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <BoxesIcon className="h-4 w-4 text-primary" />
            Exhibitor Booth Directory
          </CardTitle>
          <Badge variant="secondary" className="font-normal">
            {capturedCount}/{boothStations.length} ({pct}%)
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {/* Progress bar */}
        <div className="mb-3 h-2 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
          {boothStations.map((station) => (
            <ExhibitorCard
              key={station.id}
              station={station}
              challenges={challenges}
              captured={capturedStationIds.includes(station.id)}
              completedChallengeIds={completedChallengeIds}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Hunt Quick Actions (3D Twin + AR Scanner + Map) ─────────────────────

export function HuntQuickActions({ slug }: { slug: string }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <Button asChild size="lg" variant="outline" className="h-12">
        <Link to={`/events/${slug}/twin`}>
          <BoxesIcon className="mr-2 h-5 w-5" aria-hidden />
          3D Twin
        </Link>
      </Button>
      <Button asChild size="lg" variant="outline" className="h-12">
        <Link to="/ar">
          <ScanLineIcon className="mr-2 h-5 w-5" aria-hidden />
          AR / CV Scanner
        </Link>
      </Button>
      <Button asChild size="lg" variant="outline" className="h-12">
        <Link to={`/hunt/${slug}/leaderboard/live`}>
          <TrophyIcon className="mr-2 h-5 w-5" aria-hidden />
          Live Scores
        </Link>
      </Button>
    </div>
  );
}

// ── Booth Capture Stats (for leaderboard enhancement) ───────────────────

export function BoothCaptureStats({
  stations,
  capturedStationIds,
}: {
  stations: PublicStation[];
  capturedStationIds: string[];
}) {
  const booths = stations.filter((s) => isBoothStation(s));
  const captured = booths.filter((s) => capturedStationIds.includes(s.id));
  const rooms = stations.filter((s) => !isBoothStation(s) && s.name !== 'Registration Desk');
  const capturedRooms = rooms.filter((s) => capturedStationIds.includes(s.id));

  const stats = [
    { label: 'Booths', captured: captured.length, total: booths.length, icon: BoxesIcon, color: 'text-blue-500' },
    { label: 'Session Rooms', captured: capturedRooms.length, total: rooms.length, icon: Building2Icon, color: 'text-purple-500' },
  ];

  return (
    <div className="grid grid-cols-2 gap-3">
      {stats.map((s) => {
        const Icon = s.icon;
        const pct = s.total > 0 ? Math.round((s.captured / s.total) * 100) : 0;
        return (
          <div key={s.label} className="flex items-center gap-3 rounded-lg border border-border bg-muted/20 p-3">
            <Icon className={cn('h-5 w-5', s.color)} />
            <div className="flex-1">
              <div className="flex items-baseline justify-between">
                <span className="text-xs font-medium text-muted-foreground">{s.label}</span>
                <span className="text-sm font-bold tabular-nums">{s.captured}/{s.total}</span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
